// Só estes nomes do pacote: `LUT3DEffect`, `LookupTexture` e os `SMAA*` criam workers de blob, que a
// CSP do build bloqueia (`post-fence.test.ts`). A vinheta é nossa (`VinhetaEffect`, abaixo).
import { BloomEffect, BlendFunction, Effect, EffectComposer, EffectPass, RenderPass, SSAOEffect } from "postprocessing";
import { AberracaoEffect, IndirectEffect, IndirectLightPass, NormalsFromDepthPass, SolEffect } from "./post-cinema.ts";
import { HalfFloatType, Uniform, UnsignedByteType, Vector2, Vector3, Vector4, type Camera, type Scene, type Texture, type WebGLRenderer, type WebGLRenderTarget } from "three";
import { MOTION, msaaSamples, POST_STYLE, SHOCK, type GradeParams, type PostPlan } from "./post-model.ts";

/**
 * Onda de choque (F94, só no Alto): até duas ondas (`waveA`, `waveB`: centro em uv, raio e amplitude, na
 * altura da tela; `waveWidth`: as espessuras), corrigidas pelo `aspect` do passe. Dentro do anel, o pixel
 * lê o buffer de entrada mais perto do centro (a imagem abre para fora). Não transforma o UV do passe
 * (`mainUv`): o que um efeito anterior do passe já desenhou fica onde estava (a diferença dele para a
 * cena vai somada), e o bloom e a cor vêm depois, sem deslocar o halo.
 * Onde dois anéis se cruzam, a soma fica em `fold` da maior espessura (a imagem não dobra), e perto da borda
 * da tela o deslocamento encolhe para a leitura não sair dela (sem borrar a borda com o centro fora da tela).
 * Fora dos anéis, ou parada (amplitude 0), a saída é a entrada: o quadro sai igual ao de antes.
 * Espelho: `shockDisplacement`.
 */
const ONDA = /* glsl */ `
uniform vec4 waveA;
uniform vec4 waveB;
uniform vec2 waveWidth;

// Deslocamento (uv) do anel em uv, para fora do centro; 0 fora dele.
vec2 ring(const in vec4 wave, const in float width, const in vec2 uv) {
  if (wave.w <= 0.0) return vec2(0.0);
  vec2 d = (uv - wave.xy) * vec2(aspect, 1.0);
  float len = length(d);
  float x = (len - wave.z) / width;
  if (abs(x) >= 1.0 || len <= 0.0) return vec2(0.0);
  float s = 1.0 - x * x;
  return d / len * (wave.w * s * s) * vec2(1.0 / aspect, 1.0);
}

void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  vec2 a = ring(waveA, waveWidth.x, uv);
  vec2 b = ring(waveB, waveWidth.y, uv);
  vec2 offset = a + b;
  if (offset.x == 0.0 && offset.y == 0.0) {
    outputColor = inputColor;
    return;
  }
  // A soma no máximo em ${SHOCK.fold.toFixed(2)} da maior espessura entre os anéis que deslocam.
  float widest = max(a.x != 0.0 || a.y != 0.0 ? waveWidth.x : 0.0, b.x != 0.0 || b.y != 0.0 ? waveWidth.y : 0.0);
  offset *= min(1.0, ${SHOCK.fold.toFixed(2)} * widest / length(offset * vec2(aspect, 1.0)));
  // Perto da borda, encolhe: a leitura (uv − deslocamento) fica dentro da tela.
  float edge = min(min(uv.x, 1.0 - uv.x), min(uv.y, 1.0 - uv.y));
  offset *= clamp(edge / max(abs(offset.x), abs(offset.y)), 0.0, 1.0);
  vec3 here = texture2D(inputBuffer, uv).rgb;
  vec3 moved = texture2D(inputBuffer, uv - offset).rgb;
  outputColor = vec4(max(moved + inputColor.rgb - here, 0.0), inputColor.a);
}
`;

export class OndaEffect extends Effect {
  constructor() {
    super("OndaEffect", ONDA, {
      blendFunction: BlendFunction.NORMAL,
      uniforms: new Map<string, Uniform>([
        ["waveA", new Uniform(new Vector4())],
        ["waveB", new Uniform(new Vector4())],
        ["waveWidth", new Uniform(new Vector2(SHOCK.minWidth, SHOCK.minWidth))],
      ]),
    });
  }

  /** O quadro das ondas (`Shockwaves.write`: `SHOCK_FLOATS` números). */
  set waves(d: Float32Array) {
    (this.uniforms.get("waveA") as Uniform<Vector4>).value.set(d[0]!, d[1]!, d[2]!, d[3]!);
    (this.uniforms.get("waveB") as Uniform<Vector4>).value.set(d[4]!, d[5]!, d[6]!, d[7]!);
    (this.uniforms.get("waveWidth") as Uniform<Vector2>).value.set(d[8]!, d[9]!);
  }
}

/**
 * Desfoque de movimento (Fase 14, a referência do golpe), sem buffer de velocidade: a média de ${MOTION.taps}
 * leituras do buffer de entrada ao longo de um passo — o rastro da corrida (`streak.xy`, longe do corpo em
 * `streak.zw`, que fica nítido na máscara) mais o zoom do golpe forte (a partir de `zoom.xy`, força
 * `zoom.z`). Como a onda, soma a diferença da média para o pixel de entrada ao que os efeitos antes dele
 * já fizeram; parado (tudo 0), a saída é a entrada. Espelho: `motionStep`.
 */
const MOVIMENTO = /* glsl */ `
uniform vec4 streak;
uniform vec4 zoom;

void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  if (streak.x == 0.0 && streak.y == 0.0 && zoom.z == 0.0) {
    outputColor = inputColor;
    return;
  }
  float d = length((uv - streak.zw) * vec2(aspect, 1.0));
  vec2 step = streak.xy * smoothstep(${MOTION.mask[0].toFixed(3)}, ${MOTION.mask[1].toFixed(3)}, d) + (uv - zoom.xy) * zoom.z;
  vec3 here = texture2D(inputBuffer, uv).rgb;
  vec3 sum = vec3(0.0);
  for (int i = 0; i < ${MOTION.taps}; i++) {
    float t = (float(i) + 0.5) / ${MOTION.taps.toFixed(1)} - 0.5;
    sum += texture2D(inputBuffer, clamp(uv - step * t, vec2(0.0), vec2(1.0))).rgb;
  }
  outputColor = vec4(max(sum / ${MOTION.taps.toFixed(1)} + inputColor.rgb - here, 0.0), inputColor.a);
}
`;

export class MovimentoEffect extends Effect {
  constructor() {
    super("MovimentoEffect", MOVIMENTO, {
      blendFunction: BlendFunction.NORMAL,
      uniforms: new Map<string, Uniform>([
        ["streak", new Uniform(new Vector4(0, 0, 0.5, 0.5))],
        ["zoom", new Uniform(new Vector4())],
      ]),
    });
  }

  /** O quadro do desfoque (`MotionBlur.write`: `MOTION_FLOATS` números). */
  set motion(d: Float32Array) {
    (this.uniforms.get("streak") as Uniform<Vector4>).value.set(d[0]!, d[1]!, d[2]!, d[3]!);
    (this.uniforms.get("zoom") as Uniform<Vector4>).value.set(d[4]!, d[5]!, d[6]!, d[7]!);
  }
}

/**
 * Correção de cor (F52) em sRGB, sem tone mapping global: clareia os meios-tons, uma curva em S
 * suave, um pouco mais de saturação e de calor (`grade` = luz, contraste, saturação, calor; zeros
 * deixam a cor igual). Antes, o que passou de 1 para em 1 por canal (o que o canvas faria); com o
 * bloom (`hdr.x`), os pixels da cena que entram no bloom (a luminância da cena, lida do buffer de
 * entrada antes dos efeitos, acima do limiar `hdr.y`, com a suavidade `hdr.z`: a mesma conta do
 * pacote) são divididos pelo maior canal, já com o halo somado: os emissivos que o Alto sobe acima
 * de 1 voltam ao matiz deles em vez de amarelar, e o resto (o halo sobre o chão, o céu) fica como
 * no Médio, parado em 1 por canal. Sai com alfa 1. Espelho em TypeScript: `gradeLinear` (a cor da
 * cena, sem o halo).
 */
const GRADE = /* glsl */ `
uniform vec4 grade;
uniform vec3 hdr;

vec3 toSrgb(const in vec3 c) {
  return mix(c * 12.92, 1.055 * pow(c, vec3(1.0 / 2.4)) - 0.055, step(vec3(0.0031308), c));
}

vec3 toLinear(const in vec3 c) {
  return mix(c / 12.92, pow((c + 0.055) / 1.055, vec3(2.4)), step(vec3(0.04045), c));
}

void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  vec3 c = max(inputColor.rgb, 0.0);
  float peak = max(c.r, max(c.g, c.b));
  vec3 scene = max(texture2D(inputBuffer, uv).rgb, 0.0);
  float bloomed = hdr.x * smoothstep(hdr.y, hdr.y + hdr.z, dot(scene, vec3(0.2126, 0.7152, 0.0722)));
  c = mix(min(c, 1.0), c / max(peak, 1.0), bloomed);
  vec3 s = toSrgb(c);
  s = pow(s, vec3(1.0 / (1.0 + grade.x)));
  s += (s * s * (3.0 - 2.0 * s) - s) * grade.y;
  float l = dot(s, vec3(0.2126, 0.7152, 0.0722));
  s = l + (s - l) * (1.0 + grade.z);
  s *= vec3(1.0 + grade.w, 1.0 + grade.w * 0.3, 1.0 - grade.w);
  outputColor = vec4(toLinear(clamp(s, 0.0, 1.0)), 1.0);
}
`;

export class GradeEffect extends Effect {
  constructor() {
    super("GradeEffect", GRADE, {
      blendFunction: BlendFunction.NORMAL,
      uniforms: new Map<string, Uniform>([
        ["grade", new Uniform(new Vector4())],
        ["hdr", new Uniform(new Vector3(0, POST_STYLE.threshold, POST_STYLE.smoothing))],
      ]),
    });
  }

  set params(p: GradeParams) {
    (this.uniforms.get("grade") as Uniform<Vector4>).value.set(p.light, p.contrast, p.saturation, p.warmth);
  }

  set hdr(on: boolean) {
    (this.uniforms.get("hdr") as Uniform<Vector3>).value.x = on ? 1 : 0;
  }
}

/**
 * Vinheta (F52): a conta da técnica padrão do `VignetteEffect` do pacote (`offset`, `darkness`: só os
 * cantos escurecem), com as bordas do `smoothstep` em ordem. O do pacote chama `smoothstep(0.8,
 * 0.799·offset, d)`, com `edge0 > edge1`, indefinido em GLSL ES 3.0 (a regra dos nossos shaders,
 * `render.md`); `1 − smoothstep(0.799·offset, 0.8, d)` dá o mesmo número. Espelho: `vignetteFactor`.
 */
const VINHETA = /* glsl */ `
uniform vec2 vignette;

void mainImage(const in vec4 inputColor, const in vec2 uv, out vec4 outputColor) {
  float d = distance(uv, vec2(0.5)) * (vignette.x + vignette.y);
  outputColor = vec4(inputColor.rgb * (1.0 - smoothstep(vignette.x * 0.799, 0.8, d)), inputColor.a);
}
`;

export class VinhetaEffect extends Effect {
  constructor() {
    super("VinhetaEffect", VINHETA, {
      blendFunction: BlendFunction.NORMAL,
      uniforms: new Map<string, Uniform>([["vignette", new Uniform(new Vector2(POST_STYLE.vignetteOffset, POST_STYLE.vignetteDarkness))]]),
    });
  }

  /** A força (0 apaga). */
  set darkness(value: number) {
    (this.uniforms.get("vignette") as Uniform<Vector2>).value.y = value;
  }
}

/** O compositor de um plano, montado (o `PostFx` só cuida do ciclo de vida e do aperto de mão). */
export interface PostPipeline {
  /** O alvo em que a cena é desenhada (a pré-compilação liga o mesmo formato de saída). */
  readonly sceneTarget: WebGLRenderTarget;
  /** Com tamanho fixo (a sonda): o alvo com a imagem final, que o último passe não manda ao canvas. */
  readonly output: WebGLRenderTarget;
  readonly multisampling: number;
  setView(scene: Scene, camera: Camera): void;
  /** Acompanha o tamanho do canvas (e o multisampling com ele). */
  fit(): void;
  setParams(p: { bloom: number; vignette: number; grade: GradeParams; reduceFlashes?: boolean }): void;
  /** O sol na tela (Fase 13): uv, cor linear com a força, e se está à frente da câmera. */
  setSun(uv: Vector2, color: Vector3, front: boolean): void;
  /** O quadro das ondas de choque (F94; sem a onda no plano, nada). */
  setWaves(data: Float32Array): void;
  /** O quadro do desfoque de movimento (Fase 14; sem ele no plano, nada). */
  setMotion(data: Float32Array): void;
  render(dt: number): void;
  dispose(): void;
}

export type PipelineFactory = (renderer: WebGLRenderer, plan: PostPlan, scene: Scene, camera: Camera, size?: readonly [number, number]) => PostPipeline;

/**
 * Amostras de multisampling que o alvo do compositor aceita: o menor máximo entre o formato da cor
 * (RGBA16F ou SRGB8_ALPHA8) e o da profundidade (a textura de profundidade em float vira
 * DEPTH_COMPONENT32F com multisampling). O pacote só confere o `MAX_SAMPLES` geral, e um formato com
 * menos amostras deixaria o alvo incompleto (tela preta).
 */
function supportedSamples(gl: WebGL2RenderingContext, float: boolean): number {
  const max = (format: number): number => {
    const list = gl.getInternalformatParameter(gl.RENDERBUFFER, format, gl.SAMPLES) as Int32Array | null;
    return list && list.length > 0 ? Math.max(...list) : 0;
  };
  return Math.min(max(float ? gl.RGBA16F : gl.SRGB8_ALPHA8), max(gl.DEPTH_COMPONENT32F));
}

/**
 * Monta o compositor do plano: buffers em meio-float (ou 8 bits sRGB), um `RenderPass` que limpa o
 * alfa em 1 e um `EffectPass` com onda (Alto), bloom, cor e vinheta. Com `size` (a sonda), tamanho
 * fixo, sem multisampling e sem desenhar no canvas; senão o tamanho segue o canvas (`fit`). O pacote
 * desliga `renderer.autoClear` ao nascer: quem chama devolve.
 */
export const createPipeline: PipelineFactory = (renderer, plan, scene, camera, size) => {
  const drawing = renderer.getDrawingBufferSize(new Vector2());
  const maxSamples = supportedSamples(renderer.getContext() as WebGL2RenderingContext, plan.float);
  const composer = new EffectComposer(renderer, { frameBufferType: plan.float ? HalfFloatType : UnsignedByteType, multisampling: size ? 0 : msaaSamples(drawing.x, drawing.y, maxSamples) });
  if (size) {
    // Antes dos passes: o alvo da profundidade nasce com o tamanho do buffer de entrada.
    composer.inputBuffer.setSize(size[0], size[1]);
    composer.outputBuffer.setSize(size[0], size[1]);
  }
  const renderPass = new RenderPass(scene, camera);
  renderPass.clearPass.overrideClearAlpha = 1;
  // Fase 13 (G5): a luz (normais pela profundidade → SSAO e, no Alto, reflexo e luz indireta em meia
  // resolução), o sol na lente e o passe de antes, com a aberração no fim dele.
  const normals = plan.ao || plan.indirect ? new NormalsFromDepthPass(camera) : undefined;
  const indirect = plan.indirect && normals ? new IndirectLightPass(camera, normals.texture) : undefined;
  if (indirect) indirect.strengths = { ssr: POST_STYLE.ssr, gi: POST_STYLE.gi, radius: POST_STYLE.giRadius };
  const ao = plan.ao && normals
    ? new SSAOEffect(camera, normals.texture, {
        blendFunction: BlendFunction.MULTIPLY,
        samples: 11,
        rings: 4,
        worldDistanceThreshold: 70,
        worldDistanceFalloff: 25,
        worldProximityThreshold: 0.5,
        worldProximityFalloff: 0.3,
        luminanceInfluence: 0.75,
        radius: 0.045,
        intensity: 1.5,
        bias: 0.025,
        fade: 0.02,
        resolutionScale: 0.5,
      })
    : undefined;
  const lightEffects: Effect[] = [ao, indirect ? new IndirectEffect(indirect) : undefined].filter((e) => e !== undefined);
  const sol = plan.rays || plan.flare ? new SolEffect() : undefined;
  const aberration = plan.aberration ? new AberracaoEffect(POST_STYLE.aberration) : undefined;
  // Somado (o padrão do pacote, `SCREEN`, subtrai num canal acima de 1: o emissivo subido amarelaria).
  const bloom = plan.bloom ? new BloomEffect({ blendFunction: BlendFunction.ADD, mipmapBlur: true, luminanceThreshold: POST_STYLE.threshold, luminanceSmoothing: POST_STYLE.smoothing, intensity: POST_STYLE.bloom, levels: plan.levels, radius: POST_STYLE.bloomRadius }) : undefined;
  const grade = new GradeEffect();
  grade.hdr = plan.bloom;
  const vignette = plan.vignette ? new VinhetaEffect() : undefined;
  // A onda primeiro (o pacote ordena pelos atributos e mantém a ordem dos iguais): ela lê o buffer de
  // entrada, e o bloom, a cor e a vinheta continuam valendo sobre a imagem deslocada.
  const onda = plan.onda ? new OndaEffect() : undefined;
  // O desfoque de movimento (Fase 14) depois da onda, pelo mesmo caminho (lê o buffer de entrada).
  const movimento = plan.motion ? new MovimentoEffect() : undefined;
  const effects: Effect[] = [onda, movimento, bloom, grade, vignette, aberration].filter((e) => e !== undefined);
  const effectPass = new EffectPass(camera, ...effects);
  composer.addPass(renderPass);
  if (normals) composer.addPass(normals);
  if (indirect) composer.addPass(indirect);
  if (lightEffects.length > 0) composer.addPass(new EffectPass(camera, ...lightEffects));
  if (sol) composer.addPass(new EffectPass(camera, sol));
  composer.addPass(effectPass);
  // Em 8 bits, o pacote declara o buffer de entrada `lowp`: num celular, a cor linear do escuro (lida
  // de um alvo sRGB) perderia precisão antes da correção de cor (faixas na noite).
  if (!plan.float) {
    for (const pass of composer.passes) {
      if (!(pass instanceof EffectPass)) continue;
      const material = pass.fullscreenMaterial;
      material.defines = { ...material.defines, FRAMEBUFFER_PRECISION_HIGH: "1" };
      material.needsUpdate = true;
    }
  }
  // Onde termina a imagem (a sonda lê sem desenhar no canvas): cada passe que troca os buffers alterna.
  const swaps = composer.passes.filter((pass) => pass.needsSwap && pass !== renderPass).length;
  const last = composer.passes[composer.passes.length - 1]!;
  if (size) {
    for (const pass of composer.passes) pass.setSize(size[0], size[1]);
    last.renderToScreen = false;
  }
  let flareOn = plan.flare;
  let width = size ? size[0] : drawing.x;
  let height = size ? size[1] : drawing.y;
  let view = { scene, camera };
  return {
    get sceneTarget() {
      return composer.inputBuffer;
    },
    get output() {
      return swaps % 2 === 1 ? composer.outputBuffer : composer.inputBuffer;
    },
    get multisampling() {
      return composer.multisampling;
    },
    setView(nextScene, nextCamera) {
      if (view.scene === nextScene && view.camera === nextCamera) return;
      view = { scene: nextScene, camera: nextCamera };
      composer.setMainScene(nextScene);
      composer.setMainCamera(nextCamera);
    },
    fit() {
      if (size) return;
      renderer.getDrawingBufferSize(drawing);
      if (drawing.x === width && drawing.y === height) return;
      width = drawing.x;
      height = drawing.y;
      composer.multisampling = msaaSamples(width, height, maxSamples);
      // O tamanho atual do renderer: o compositor só refaz os buffers (não mexe no canvas).
      const css = renderer.getSize(new Vector2());
      composer.setSize(css.x, css.y);
    },
    setParams(p) {
      if (bloom) bloom.intensity = p.bloom;
      if (vignette) vignette.darkness = p.vignette;
      grade.params = p.grade;
      // "Reduzir lampejos": sem reflexo de lente e sem aberração.
      flareOn = plan.flare && p.reduceFlashes !== true;
      if (aberration) aberration.offset = p.reduceFlashes ? 0 : POST_STYLE.aberration;
    },
    setSun(uv, color, front) {
      if (sol) sol.sun = { uv, color, rays: plan.rays ? POST_STYLE.rays : 0, flare: flareOn ? POST_STYLE.flare : 0, front };
    },
    setWaves(data) {
      if (onda) onda.waves = data;
    },
    setMotion(data) {
      if (movimento) movimento.motion = data;
    },
    render(dt) {
      composer.render(dt);
    },
    dispose() {
      // O pacote não solta a textura de ruído do SSAO (64², guardada só no uniform do material): sem isto,
      // cada pipeline com oclusão deixava uma na GPU (`lifetime.spec`).
      (ao?.ssaoMaterial.uniforms["noiseTexture"]?.value as Texture | null | undefined)?.dispose();
      composer.dispose();
    },
  };
};
